// src/components/compliance/RequirementAssignmentsDrawer.tsx
//
// Manage the assignments (which subjects this requirement applies to)
// for a single requirement. Opened from the Requirements table.
//
// Subject types (mirrors backend):
//   contact | provider_user | provider_role |
//   department_circle | central_team | workspace
//
// Backend does polymorphic subject validation — we send subjectType +
// subjectId as strings; the UI helps operators pick the id from a
// searchable list when the primitive is known (contact, user, role,
// department). For `workspace` we auto-fill the id.
//
// isRequired=false is surfaced as an EXCLUSION toggle — mirrors the
// eligibility-engine semantics.

import { useMemo, useState } from 'react';
import {
  useCreateComplianceAssignmentMutation,
  useDeleteComplianceAssignmentMutation,
  useEndComplianceAssignmentMutation,
  useGetComplianceAssignmentsQuery,
  useUpdateComplianceAssignmentMutation,
} from '../../api/complianceApi';
import { useGetContactsQuery } from '../../api/contactsApi';
import { useGetProviderRolesQuery } from '../../api/providerAssignmentsApi';
import { useGetCirclesQuery, useGetWorkspaceCentralTeamsQuery } from '../../api/circlesApi';
import { useGetTeamMembersQuery } from '../../api/teamApi';
import LoadingSpinner from '../common/LoadingSpinner';
import Button from '../common/Button';
import { SUBJECT_TYPE_LABEL, formatShortDate, messageFromComplianceError } from './complianceDisplay';
import type {
  ComplianceRequirement,
  ComplianceRequirementAssignment,
  ComplianceSubjectType,
} from '../../types/api';

interface RequirementAssignmentsDrawerProps {
  requirement: ComplianceRequirement;
  workspaceId: string;
  onClose: () => void;
}

const SUBJECT_TYPES: ComplianceSubjectType[] = [
  'contact',
  'provider_user',
  'provider_role',
  'department_circle',
  'central_team',
  'workspace',
];

export default function RequirementAssignmentsDrawer({
  requirement,
  workspaceId,
  onClose,
}: RequirementAssignmentsDrawerProps) {
  const { data, isLoading, error, refetch } = useGetComplianceAssignmentsQuery({
    requirementId: requirement.id,
    limit: 200,
  });
  const [adding, setAdding] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const [endAssignment] = useEndComplianceAssignmentMutation();
  const [deleteAssignment] = useDeleteComplianceAssignmentMutation();
  const [updateAssignment] = useUpdateComplianceAssignmentMutation();

  const rows = data?.rows ?? [];

  const handleEnd = async (row: ComplianceRequirementAssignment) => {
    try {
      await endAssignment(row.id).unwrap();
    } catch (e) {
      setBanner(messageFromComplianceError(e));
    }
  };
  const handleDelete = async (row: ComplianceRequirementAssignment) => {
    try {
      await deleteAssignment(row.id).unwrap();
    } catch (e) {
      setBanner(messageFromComplianceError(e));
    }
  };
  const handleToggleExclusion = async (row: ComplianceRequirementAssignment) => {
    try {
      await updateAssignment({
        id: row.id,
        patch: { isRequired: !row.isRequired },
      }).unwrap();
    } catch (e) {
      setBanner(messageFromComplianceError(e));
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-stretch justify-end bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compliance-assignments-drawer-title"
    >
      <aside className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col" data-testid="compliance-assignments-drawer">
        <header className="border-b border-gray-100 px-5 py-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="compliance-assignments-drawer-title" className="text-lg font-semibold text-gray-900">
              Assignments for "{requirement.name}"
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Apply this requirement to specific subjects. Exclusions (isRequired=false) remove the requirement for a subject inside a broader scope.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close drawer">✕</button>
        </header>

        <div className="p-5 space-y-4">
          {banner && (
            <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {banner}
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {rows.length} assignment{rows.length === 1 ? '' : 's'}
            </span>
            <Button type="button" onClick={() => setAdding(true)} data-testid="compliance-assignment-add-btn">
              Assign to subject
            </Button>
          </div>

          {isLoading && <LoadingSpinner />}
          {error && (
            <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {messageFromComplianceError(error)}
            </div>
          )}

          {rows.length > 0 && (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
              {rows.map((row) => {
                const ended =
                  row.effectiveUntil !== null &&
                  new Date(row.effectiveUntil).getTime() <= Date.now();
                return (
                  <li key={row.id} className="p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`compliance-assignment-row-${row.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-gray-900">
                        <span className="font-medium">{SUBJECT_TYPE_LABEL[row.subjectType]}</span>
                        {' '}·{' '}
                        <span className="font-mono text-xs text-gray-600">{row.subjectId}</span>
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500">
                        Effective {formatShortDate(row.effectiveFrom)}
                        {row.effectiveUntil ? ` – ${formatShortDate(row.effectiveUntil)}` : ' – ongoing'}
                        {!row.isRequired && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                            Exclusion
                          </span>
                        )}
                        {ended && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 ring-1 ring-gray-200">
                            Ended
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        className="text-xs text-indigo-700 hover:underline"
                        onClick={() => handleToggleExclusion(row)}
                        data-testid={`compliance-assignment-toggle-${row.id}`}
                      >
                        {row.isRequired ? 'Mark as exclusion' : 'Mark as required'}
                      </button>
                      {!ended && (
                        <button
                          type="button"
                          className="text-xs text-amber-700 hover:underline"
                          onClick={() => handleEnd(row)}
                          data-testid={`compliance-assignment-end-${row.id}`}
                        >
                          End now
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-xs text-red-700 hover:underline"
                        onClick={() => handleDelete(row)}
                        data-testid={`compliance-assignment-delete-${row.id}`}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {adding && (
          <AssignmentAddDialog
            requirement={requirement}
            workspaceId={workspaceId}
            onClose={() => setAdding(false)}
            onError={setBanner}
          />
        )}
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AssignmentAddDialog
// ---------------------------------------------------------------------------

function AssignmentAddDialog({
  requirement,
  workspaceId,
  onClose,
  onError,
}: {
  requirement: ComplianceRequirement;
  workspaceId: string;
  onClose: () => void;
  onError: (msg: string) => void;
}) {
  const [subjectType, setSubjectType] = useState<ComplianceSubjectType>('provider_user');
  const [subjectId, setSubjectId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveUntil, setEffectiveUntil] = useState('');
  const [isRequired, setIsRequired] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [create, state] = useCreateComplianceAssignmentMutation();

  // Auto-fill subject id for workspace scope.
  const effectiveSubjectId =
    subjectType === 'workspace' ? workspaceId : subjectId.trim();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      await create({
        requirementId: requirement.id,
        subjectType,
        subjectId: effectiveSubjectId,
        effectiveFrom: effectiveFrom ? new Date(effectiveFrom).toISOString() : undefined,
        effectiveUntil: effectiveUntil ? new Date(effectiveUntil).toISOString() : null,
        isRequired,
      }).unwrap();
      onClose();
    } catch (e2) {
      const msg = messageFromComplianceError(e2);
      setErr(msg);
      onError(msg);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <form onSubmit={handle} className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-3" data-testid="compliance-assignment-add-dialog">
        <h3 className="text-lg font-semibold text-gray-900">Assign requirement to subject</h3>
        {err && (
          <div role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {err}
          </div>
        )}

        <div>
          <label htmlFor="asg-subject-type" className="block text-xs font-medium text-gray-700 mb-1">Subject type</label>
          <select
            id="asg-subject-type"
            value={subjectType}
            onChange={(e) => {
              const next = e.target.value as ComplianceSubjectType;
              setSubjectType(next);
              if (next !== 'workspace') setSubjectId('');
            }}
            className="w-full rounded border-gray-300 text-sm"
            data-testid="compliance-assignment-subject-type"
          >
            {SUBJECT_TYPES.map((t) => (
              <option key={t} value={t}>{SUBJECT_TYPE_LABEL[t]}</option>
            ))}
          </select>
        </div>

        <SubjectIdPicker
          subjectType={subjectType}
          workspaceId={workspaceId}
          value={subjectId}
          onChange={setSubjectId}
        />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="asg-from" className="block text-xs font-medium text-gray-700 mb-1">Effective from</label>
            <input id="asg-from" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} className="w-full rounded border-gray-300 text-sm" />
          </div>
          <div>
            <label htmlFor="asg-until" className="block text-xs font-medium text-gray-700 mb-1">Effective until (optional)</label>
            <input id="asg-until" type="date" value={effectiveUntil} onChange={(e) => setEffectiveUntil(e.target.value)} className="w-full rounded border-gray-300 text-sm" />
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!isRequired}
            onChange={(e) => setIsRequired(!e.target.checked)}
            data-testid="compliance-assignment-exclusion-toggle"
          />
          <span>
            Mark as an <strong>exclusion</strong> (removes this requirement for the subject inside a broader scope).
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={state.isLoading || !effectiveSubjectId} data-testid="compliance-assignment-add-confirm">
            {state.isLoading ? 'Saving…' : 'Assign'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SubjectIdPicker — different UI per subject type
// ---------------------------------------------------------------------------

function SubjectIdPicker({
  subjectType,
  workspaceId,
  value,
  onChange,
}: {
  subjectType: ComplianceSubjectType;
  workspaceId: string;
  value: string;
  onChange: (v: string) => void;
}) {
  if (subjectType === 'workspace') {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
        <input
          value={workspaceId}
          disabled
          className="w-full rounded border-gray-300 text-sm bg-gray-100 font-mono"
          data-testid="compliance-assignment-subject-id-workspace"
        />
        <p className="text-xs text-gray-500 mt-1">The whole workspace.</p>
      </div>
    );
  }
  if (subjectType === 'contact') {
    return <ContactPicker value={value} onChange={onChange} />;
  }
  if (subjectType === 'provider_user') {
    return <UserPicker workspaceId={workspaceId} value={value} onChange={onChange} />;
  }
  if (subjectType === 'provider_role') {
    return <ProviderRolePicker value={value} onChange={onChange} />;
  }
  if (subjectType === 'department_circle') {
    return <DepartmentCirclePicker value={value} onChange={onChange} />;
  }
  if (subjectType === 'central_team') {
    return <CentralTeamPicker workspaceId={workspaceId} value={value} onChange={onChange} />;
  }
  return null;
}

function ContactPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data, isLoading } = useGetContactsQuery({ limit: 200 });
  return (
    <PickerSelect
      label="Contact"
      loading={isLoading}
      options={(data?.contacts ?? []).map((c) => ({
        id: c.id,
        label: c.fullName || c.email || c.id,
      }))}
      value={value}
      onChange={onChange}
      testId="compliance-assignment-subject-id-contact"
    />
  );
}

function UserPicker({
  workspaceId,
  value,
  onChange,
}: {
  workspaceId: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { data, isLoading } = useGetTeamMembersQuery({ workspaceId });
  return (
    <PickerSelect
      label="Provider (user)"
      loading={isLoading}
      options={(data ?? []).map((m) => ({
        id: m.userId,
        label: m.fullName || m.email || m.userId,
      }))}
      value={value}
      onChange={onChange}
      testId="compliance-assignment-subject-id-user"
    />
  );
}

function ProviderRolePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data, isLoading } = useGetProviderRolesQuery();
  return (
    <PickerSelect
      label="Provider Role"
      loading={isLoading}
      options={(data ?? []).map((r) => ({ id: r.id, label: r.name }))}
      value={value}
      onChange={onChange}
      testId="compliance-assignment-subject-id-role"
    />
  );
}

function DepartmentCirclePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data, isLoading } = useGetCirclesQuery({ type: 'department' });
  return (
    <PickerSelect
      label="Department circle"
      loading={isLoading}
      options={(data ?? []).map((c) => ({ id: c.id, label: c.name }))}
      value={value}
      onChange={onChange}
      testId="compliance-assignment-subject-id-department"
    />
  );
}

function CentralTeamPicker({
  workspaceId: _workspaceId,
  value,
  onChange,
}: {
  workspaceId: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { data, isLoading } = useGetWorkspaceCentralTeamsQuery();
  const teams = data?.teams ?? [];
  const unavailable = data && !data.available;
  return (
    <PickerSelect
      label="Central Team"
      loading={isLoading}
      options={teams.map((t) => ({ id: t.id, label: t.name }))}
      value={value}
      onChange={onChange}
      testId="compliance-assignment-subject-id-central-team"
      hint={
        unavailable
          ? 'Central Team SDK is not configured for this environment. You can still enter a Central Team id manually below.'
          : 'Central Team assignments are soft-checked — the linkage can be created before the team is mirrored locally.'
      }
    />
  );
}

function PickerSelect({
  label,
  loading,
  options,
  value,
  onChange,
  testId,
  hint,
}: {
  label: string;
  loading: boolean;
  options: Array<{ id: string; label: string }>;
  value: string;
  onChange: (v: string) => void;
  testId?: string;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {loading ? (
        <div className="text-xs text-gray-500">Loading…</div>
      ) : (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded border-gray-300 text-sm"
          data-testid={testId}
          required
        >
          <option value="" disabled>Choose…</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
      )}
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}
